import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Link,
  Divider,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useT } from '../i18n/useT.js';
import { useLang } from '../i18n/lang.js';
import { helpGroups } from './helpContent.js';


export function HelpPage() {
  const t = useT().help;
  const [lang] = useLang();
  const GROUPS = useMemo(() => helpGroups(lang), [lang]);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        articles: g.articles.filter(
          (a) => !q || a.q.toLowerCase().includes(q) || (a.tags ?? []).some((t) => t.includes(q)),
        ),
      })).filter((g) => g.articles.length > 0),
    [q],
  );

  const scrollTo = (id: string) => document.getElementById(`help-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      {/* Sticky table of contents (wide screens only) */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 24, width: 210, flexShrink: 0 }}>
        <Typography variant="overline" color="text.disabled">{t.onThisPage}</Typography>
        <Stack sx={{ mt: 0.5 }}>
          {GROUPS.map((g) => {
            const visible = groups.some((vg) => vg.id === g.id);
            return (
              <Link
                key={g.id}
                component="button"
                onClick={() => scrollTo(g.id)}
                underline="none"
                sx={{
                  textAlign: 'left', py: 0.6, color: visible ? 'text.secondary' : 'text.disabled',
                  '&:hover': { color: 'primary.main' }, display: 'flex', alignItems: 'center', gap: 1, fontSize: 14,
                }}
              >
                <Box sx={{ display: 'flex', '& svg': { fontSize: 18 } }}>{g.icon}</Box>
                {g.title}
              </Link>
            );
          })}
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, maxWidth: 860, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <HelpOutlineIcon color="primary" />
          <Typography variant="h5">{t.title}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t.subtitle}
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder={t.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          sx={{ mb: 3 }}
        />

        {groups.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{t.noMatch(query)}</Typography>
          </Paper>
        )}

        {groups.map((g) => (
          <Box key={g.id} id={`help-${g.id}`} sx={{ mb: 3.5, scrollMarginTop: 24 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25, color: 'primary.main' }}>
              {g.icon}
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">{g.title}</Typography>
            </Stack>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 1.25 }}>{g.blurb}</Typography>
            {g.articles.map((a, i) => (
              <Accordion
                key={`${q}-${i}`}
                disableGutters
                defaultExpanded={!!q}
                sx={{ '&:before': { display: 'none' }, mb: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', '&.Mui-expanded': { borderColor: 'primary.main' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{a.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>{a.a}</AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{t.stuck}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t.stuckBody}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip label={t.openSecurity} onClick={() => navigate('/security')} variant="outlined" />
            <Chip label={t.integrationsHub} onClick={() => navigate('/integrations')} variant="outlined" />
            <Chip label={t.configuration} onClick={() => navigate('/configuration')} variant="outlined" />
            <Link href="mailto:support@replayops.ai" sx={{ alignSelf: 'center', ml: 1 }} variant="body2">support@replayops.ai</Link>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
